import type { ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Activity,
  ArrowLeftRight,
  FileCheck2,
  IdCard,
  Layers,
  MapPin,
  Package,
  PenLine,
  ScanSearch,
  Search,
  User,
  UserCheck,
  Users,
} from "lucide-react";
import type { AiPipelineAccentStyle } from "./aiPipelineAccentStyles";
import type { AiPipelineVisualVariant } from "./aiPipelineCatalog";

export type AiPipelineRunningVisualProps = {
  variant: AiPipelineVisualVariant;
  accent: AiPipelineAccentStyle;
  activeStepIndex: number;
  stepCount: number;
  reducedMotion?: boolean;
  size?: "sm" | "md";
};

const shellPadding = {
  sm: "p-2",
  md: "p-3",
};

function clampStepIndex(activeStepIndex: number, stepCount: number): number {
  if (stepCount <= 0) return 0;
  return Math.min(Math.max(activeStepIndex, 0), stepCount - 1);
}

const STAGE_CANVAS = "relative h-14 w-14 shrink-0";

function StageCanvas({ children }: { children: ReactNode }) {
  return (
    <div className={STAGE_CANVAS}>
      <div className="absolute inset-0 flex items-center justify-center">{children}</div>
    </div>
  );
}

const ScriptStageCanvas = StageCanvas;

function ScriptPage({
  className,
  children,
  layout = "absolute",
}: {
  className: string;
  children?: ReactNode;
  layout?: "absolute" | "relative";
}) {
  return (
    <div
      className={`${layout === "absolute" ? "absolute" : "relative"} rounded-[3px] border border-border bg-gradient-to-br from-foreground/[0.06] to-foreground/[0.02] shadow-[inset_0_1px_0_rgba(15,23,42,0.06)] dark:from-foreground/[0.14] dark:to-foreground/[0.04] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] ${className}`}
    >
      {children}
    </div>
  );
}

function ScriptPageLines({ count = 4 }: { count?: number }) {
  const widths = ["w-full", "w-[88%]", "w-full", "w-[72%]"] as const;

  return (
    <div className="flex flex-col gap-[3px] px-2 pt-3">
      {widths.slice(0, count).map((width, index) => (
        <div key={index} className={`h-px rounded-full bg-muted/70 ${width}`} />
      ))}
    </div>
  );
}

function ScriptPageIcon({
  className,
  children,
  layout = "absolute",
}: {
  className: string;
  children: ReactNode;
  layout?: "absolute" | "relative";
}) {
  return (
    <ScriptPage className={className} layout={layout}>
      <div className="flex h-full w-full items-center justify-center">{children}</div>
    </ScriptPage>
  );
}

const scriptTagHighlightStyles = {
  brand: "border-primary bg-primary",
  sky: "border-hue-sky bg-hue-sky",
  violet: "border-hue-violet bg-hue-violet",
} as const;

function ScriptTagRow({
  tone,
  widthClass,
}: {
  tone: keyof typeof scriptTagHighlightStyles;
  widthClass: string;
}) {
  return (
    <div
      className={`mx-auto h-[5px] rounded-sm border ${scriptTagHighlightStyles[tone]} ${widthClass}`}
      aria-hidden
    />
  );
}

function ScriptPagesVisual({ step, reducedMotion }: { step: number; reducedMotion: boolean }) {
  const iconStroke = 2;

  if (step === 0) {
    return (
      <ScriptStageCanvas>
        <div className="relative h-14 w-12">
          <ScriptPage className="left-0 top-1/2 h-11 w-8 -translate-y-1/2 -rotate-[7deg] border-border opacity-90">
            <ScriptPageLines count={3} />
          </ScriptPage>
          <ScriptPage className="left-3 top-1/2 z-[1] h-11 w-8 -translate-y-1/2 rotate-[2deg] border-border">
            <ScriptPageLines />
          </ScriptPage>
          <Search
            className="pointer-events-none absolute left-1/2 top-[46%] z-10 h-[1.125rem] w-[1.125rem] -translate-x-1/2 -translate-y-1/2 text-card-foreground"
            strokeWidth={iconStroke}
            aria-hidden
          />
        </div>
      </ScriptStageCanvas>
    );
  }

  if (step === 1) {
    return (
      <ScriptStageCanvas>
        <div className="flex h-14 w-14 items-center justify-center">
          <ScriptPageIcon
            layout="relative"
            className="h-11 w-7 shrink-0 -rotate-[7deg] border-border opacity-90"
          >
            <User className="h-4 w-4 text-hue-sky" strokeWidth={iconStroke} aria-hidden />
          </ScriptPageIcon>
          <ScriptPageIcon
            layout="relative"
            className="z-[1] -ml-3.5 h-11 w-7 shrink-0 rotate-0 border-border"
          >
            <Package className="h-4 w-4 text-secondary" strokeWidth={iconStroke} aria-hidden />
          </ScriptPageIcon>
          <ScriptPageIcon
            layout="relative"
            className="-ml-3.5 h-11 w-7 shrink-0 rotate-[7deg] border-border"
          >
            <MapPin className="h-4 w-4 text-hue-violet" strokeWidth={iconStroke} aria-hidden />
          </ScriptPageIcon>
        </div>
      </ScriptStageCanvas>
    );
  }

  if (step === 2) {
    return (
      <ScriptStageCanvas>
        <div className="flex h-14 w-14 items-center justify-center">
          <ScriptPage
            layout="relative"
            className="h-11 w-8 shrink-0 -rotate-[6deg] border-border opacity-65"
          >
            <ScriptPageLines count={3} />
          </ScriptPage>
          <ScriptPage
            layout="relative"
            className="z-[1] -ml-3 h-11 w-8 shrink-0 rotate-[1deg] overflow-hidden border-border shadow-[0_4px_12px_rgba(0,0,0,0.3)]"
          >
            <div className="flex h-full flex-col items-center justify-center gap-[5px] px-2">
              <ScriptTagRow tone="brand" widthClass="w-[1.2rem]" />
              <ScriptTagRow tone="sky" widthClass="w-[0.72rem]" />
              <ScriptTagRow tone="violet" widthClass="w-[0.95rem]" />
            </div>
          </ScriptPage>
        </div>
      </ScriptStageCanvas>
    );
  }

  const syncPulse = reducedMotion ? "" : "motion-safe:animate-pulse";

  return (
    <ScriptStageCanvas>
      <div className="relative flex h-14 w-14 items-center justify-center">
        <ScriptPage layout="relative" className="h-11 w-7 shrink-0 -rotate-[7deg] border-border">
          <ScriptPageLines count={3} />
        </ScriptPage>
        <ScriptPage
          layout="relative"
          className="-ml-3.5 h-11 w-7 shrink-0 rotate-[7deg] border-border"
        >
          <ScriptPageLines count={3} />
        </ScriptPage>
        <div
          className={`pointer-events-none absolute left-1/2 top-1/2 z-10 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-0.5 ${syncPulse}`}
        >
          <div
            className="h-px w-5 bg-gradient-to-r from-transparent via-hue-sky/55 to-transparent"
            aria-hidden
          />
          <ArrowLeftRight
            className="h-4 w-4 text-card-foreground"
            strokeWidth={iconStroke}
            aria-hidden
          />
          <div
            className="h-px w-5 bg-gradient-to-r from-transparent via-hue-sky/55 to-transparent"
            aria-hidden
          />
        </div>
      </div>
    </ScriptStageCanvas>
  );
}

const CHARACTER_STAGE_ICON_CLASS = "text-card-foreground";

function CharacterProfileCard({ lines, icon }: { lines?: ReactNode; icon: ReactNode }) {
  return (
    <StageCanvas>
      <div className="relative flex h-14 w-14 items-center justify-center">
        <ScriptPage layout="relative" className="h-11 w-9 overflow-hidden border-border">
          <div className="opacity-35">{lines ?? <ScriptPageLines count={3} />}</div>
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
            {icon}
          </div>
        </ScriptPage>
      </div>
    </StageCanvas>
  );
}

function CharacterProfileDraftLines({ emphasis = "muted" }: { emphasis?: "muted" | "prominent" }) {
  if (emphasis === "prominent") {
    return (
      <div className="flex flex-col items-center gap-[4px] px-1.5 pt-2">
        <div
          className="h-2.5 w-2.5 rounded-full bg-secondary/90 shadow-[0_0_8px_rgba(251,191,36,0.55)]"
          aria-hidden
        />
        <div className="h-px w-full rounded-full bg-muted" aria-hidden />
        <div className="h-px w-[88%] rounded-full bg-secondary/75" aria-hidden />
        <div className="h-px w-[72%] rounded-full bg-muted" aria-hidden />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-[3px] px-1.5 pt-2 opacity-35">
      <div className="h-2 w-2 rounded-full bg-secondary/35" aria-hidden />
      <div className="h-px w-full rounded-full bg-muted/70" aria-hidden />
      <div className="h-px w-[80%] rounded-full bg-secondary/30" aria-hidden />
      <div className="h-px w-[65%] rounded-full bg-muted" aria-hidden />
    </div>
  );
}

function CharacterProfilesVisual({
  step,
  reducedMotion,
}: {
  step: number;
  reducedMotion: boolean;
}) {
  const iconStroke = 2.5;
  const iconClass = CHARACTER_STAGE_ICON_CLASS;
  const dualIconSize = "h-3.5 w-3.5";

  if (step === 0) {
    return (
      <CharacterProfileCard
        lines={<ScriptPageLines count={4} />}
        icon={
          <ScanSearch className={`h-5 w-5 ${iconClass}`} strokeWidth={iconStroke} aria-hidden />
        }
      />
    );
  }

  if (step === 1) {
    return (
      <StageCanvas>
        <div className="flex h-14 w-14 items-center justify-center">
          <ScriptPageIcon
            layout="relative"
            className="h-10 w-6 shrink-0 -rotate-[7deg] border-border"
          >
            <User className={`${dualIconSize} ${iconClass}`} strokeWidth={iconStroke} aria-hidden />
          </ScriptPageIcon>
          <ScriptPageIcon
            layout="relative"
            className="-ml-2.5 h-10 w-6 shrink-0 rotate-[6deg] border-border"
          >
            <IdCard
              className={`${dualIconSize} ${iconClass}`}
              strokeWidth={iconStroke}
              aria-hidden
            />
          </ScriptPageIcon>
        </div>
      </StageCanvas>
    );
  }

  if (step === 2) {
    const sideCardClass = "h-10 w-6 shrink-0 border-border opacity-50";

    return (
      <StageCanvas>
        <div className="relative flex h-14 w-14 items-center justify-center">
          <ScriptPage layout="relative" className={`-rotate-[8deg] ${sideCardClass}`}>
            <CharacterProfileDraftLines emphasis="muted" />
          </ScriptPage>
          <ScriptPage
            layout="relative"
            className="z-[1] -ml-2 h-10 w-6 shrink-0 rotate-0 border-border shadow-[0_4px_12px_rgba(0,0,0,0.28)]"
          >
            <CharacterProfileDraftLines emphasis="prominent" />
          </ScriptPage>
          <ScriptPage layout="relative" className={`rotate-[8deg] ${sideCardClass}`}>
            <CharacterProfileDraftLines emphasis="muted" />
          </ScriptPage>
        </div>
      </StageCanvas>
    );
  }

  const syncPulse = reducedMotion ? "" : "motion-safe:animate-pulse";

  return (
    <StageCanvas>
      <div className="relative flex h-14 w-14 items-center justify-center">
        <ScriptPage layout="relative" className="h-10 w-6 shrink-0 -rotate-[8deg] border-border">
          <div className="opacity-40">
            <CharacterProfileDraftLines />
          </div>
        </ScriptPage>
        <ScriptPage
          layout="relative"
          className="z-[1] -ml-2 h-10 w-6 shrink-0 rotate-0 border-border"
        >
          <div className="opacity-40">
            <CharacterProfileDraftLines />
          </div>
        </ScriptPage>
        <ScriptPage
          layout="relative"
          className="-ml-2 h-10 w-6 shrink-0 rotate-[8deg] border-border"
        >
          <div className="opacity-40">
            <CharacterProfileDraftLines />
          </div>
        </ScriptPage>
        <div
          className={`pointer-events-none absolute left-1/2 top-1/2 z-10 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-0.5 ${syncPulse}`}
        >
          <div
            className="h-px w-5 bg-gradient-to-r from-transparent via-primary/55 to-transparent"
            aria-hidden
          />
          <UserCheck className={`h-4 w-4 ${iconClass}`} strokeWidth={iconStroke} aria-hidden />
          <div
            className="h-px w-5 bg-gradient-to-r from-transparent via-primary/55 to-transparent"
            aria-hidden
          />
        </div>
      </div>
    </StageCanvas>
  );
}

const COVERAGE_STAGE_ICON_CLASS = "text-card-foreground";

function CoverageReportCard({ lines, icon }: { lines?: ReactNode; icon: ReactNode }) {
  return (
    <StageCanvas>
      <div className="relative flex h-14 w-14 items-center justify-center">
        <ScriptPage layout="relative" className="h-11 w-9 overflow-hidden border-border">
          <div className="opacity-35">{lines ?? <ScriptPageLines count={3} />}</div>
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
            {icon}
          </div>
        </ScriptPage>
      </div>
    </StageCanvas>
  );
}

function CoverageDocumentVisual({ step, reducedMotion }: { step: number; reducedMotion: boolean }) {
  const iconStroke = 2.5;
  const iconClass = COVERAGE_STAGE_ICON_CLASS;

  if (step === 0) {
    return (
      <CoverageReportCard
        lines={<ScriptPageLines count={4} />}
        icon={<Layers className={`h-5 w-5 ${iconClass}`} strokeWidth={iconStroke} aria-hidden />}
      />
    );
  }

  if (step === 1) {
    const dualIconSize = "h-3.5 w-3.5";

    return (
      <StageCanvas>
        <div className="flex h-14 w-14 items-center justify-center">
          <ScriptPageIcon
            layout="relative"
            className="h-10 w-6 shrink-0 -rotate-[7deg] border-border"
          >
            <Users
              className={`${dualIconSize} ${iconClass}`}
              strokeWidth={iconStroke}
              aria-hidden
            />
          </ScriptPageIcon>
          <ScriptPageIcon
            layout="relative"
            className="-ml-2.5 h-10 w-6 shrink-0 rotate-[6deg] border-border"
          >
            <Activity
              className={`${dualIconSize} ${iconClass}`}
              strokeWidth={iconStroke}
              aria-hidden
            />
          </ScriptPageIcon>
        </div>
      </StageCanvas>
    );
  }

  if (step === 2) {
    const draftLines = (
      <div className="flex flex-col gap-[2px] px-1.5 pt-2 opacity-40">
        <div className="h-px w-full rounded-full bg-muted/70" aria-hidden />
        <div className="h-px w-[88%] rounded-full bg-hue-violet/50" aria-hidden />
        <div className="h-px w-full rounded-full bg-muted/70" aria-hidden />
      </div>
    );

    return (
      <StageCanvas>
        <div className="relative flex h-14 w-14 items-center justify-center">
          <ScriptPage layout="relative" className="h-10 w-6 shrink-0 -rotate-[8deg] border-border">
            {draftLines}
          </ScriptPage>
          <ScriptPage
            layout="relative"
            className="z-[1] -ml-2 h-10 w-6 shrink-0 rotate-0 border-border"
          >
            {draftLines}
          </ScriptPage>
          <ScriptPage
            layout="relative"
            className="-ml-2 h-10 w-6 shrink-0 rotate-[8deg] border-border"
          >
            {draftLines}
          </ScriptPage>
          <div className="pointer-events-none absolute left-1/2 top-1/2 z-10 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-0.5">
            <div
              className="h-px w-5 bg-gradient-to-r from-transparent via-hue-violet/55 to-transparent"
              aria-hidden
            />
            <PenLine className={`h-4 w-4 ${iconClass}`} strokeWidth={iconStroke} aria-hidden />
            <div
              className="h-px w-5 bg-gradient-to-r from-transparent via-hue-violet/55 to-transparent"
              aria-hidden
            />
          </div>
        </div>
      </StageCanvas>
    );
  }

  const syncPulse = reducedMotion ? "" : "motion-safe:animate-pulse";

  return (
    <StageCanvas>
      <div className="relative flex h-14 w-14 items-center justify-center">
        <ScriptPage layout="relative" className="h-11 w-7 shrink-0 -rotate-[7deg] border-border">
          <ScriptPageLines count={3} />
        </ScriptPage>
        <ScriptPage
          layout="relative"
          className="-ml-3.5 h-11 w-7 shrink-0 rotate-[7deg] border-border"
        >
          <ScriptPageLines count={3} />
        </ScriptPage>
        <div
          className={`pointer-events-none absolute left-1/2 top-1/2 z-10 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-0.5 ${syncPulse}`}
        >
          <div
            className="h-px w-5 bg-gradient-to-r from-transparent via-hue-violet/55 to-transparent"
            aria-hidden
          />
          <FileCheck2 className={`h-4 w-4 ${iconClass}`} strokeWidth={iconStroke} aria-hidden />
          <div
            className="h-px w-5 bg-gradient-to-r from-transparent via-hue-violet/55 to-transparent"
            aria-hidden
          />
        </div>
      </div>
    </StageCanvas>
  );
}

function BudgetGridVisual({ accent, step }: { accent: AiPipelineAccentStyle; step: number }) {
  const filledThrough = step === 0 ? 2 : step === 1 ? 5 : step === 2 ? 7 : 9;

  return (
    <div className="grid h-12 w-12 grid-cols-3 grid-rows-3 gap-0.5">
      {Array.from({ length: 9 }).map((_, index) => (
        <div
          key={index}
          className={`rounded-sm ${
            index < filledThrough ? accent.visualAccent : "bg-muted"
          } ${step === 3 && index === 8 ? "ring-1 ring-hue-emerald/50" : ""}`}
        />
      ))}
    </div>
  );
}

function ScheduleStripVisual({ accent, step }: { accent: AiPipelineAccentStyle; step: number }) {
  const stripWidths = ["w-full", "w-4/5", "w-full", "w-3/5"];
  const activeThrough = step + 1;

  return (
    <div className="flex h-12 w-14 flex-col gap-1">
      {stripWidths.map((width, index) => (
        <div
          key={index}
          className={`h-2 rounded-sm ${width} ${
            index < activeThrough ? accent.visualAccent : "bg-muted"
          } ${step === 3 && index === 0 ? "opacity-100" : index < activeThrough ? "opacity-90" : ""}`}
        />
      ))}
    </div>
  );
}

function renderStageVisual(
  variant: AiPipelineVisualVariant,
  accent: AiPipelineAccentStyle,
  step: number,
  reducedMotion: boolean
) {
  switch (variant) {
    case "script_pages":
      return <ScriptPagesVisual step={step} reducedMotion={reducedMotion} />;
    case "character_profiles":
      return <CharacterProfilesVisual step={step} reducedMotion={reducedMotion} />;
    case "coverage_document":
      return <CoverageDocumentVisual step={step} reducedMotion={reducedMotion} />;
    case "budget_grid":
      return <BudgetGridVisual accent={accent} step={step} />;
    case "schedule_strip":
      return <ScheduleStripVisual accent={accent} step={step} />;
    default:
      return <div className={`h-10 w-10 rounded-full ${accent.visualAccent}`} />;
  }
}

const motionPulse = {
  animate: { y: [0, -4, 0], opacity: [0.85, 1, 0.85] },
  transition: { duration: 2.2, repeat: Infinity, ease: "easeInOut" as const },
};

export const AiPipelineRunningVisual = ({
  variant,
  accent,
  activeStepIndex,
  stepCount,
  reducedMotion = false,
  size = "md",
}: AiPipelineRunningVisualProps) => {
  const step = clampStepIndex(activeStepIndex, stepCount);

  const inner = (
    <AnimatePresence mode="wait">
      <motion.div
        key={`${variant}-${step}`}
        initial={reducedMotion ? false : { opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={reducedMotion ? undefined : { opacity: 0, scale: 0.92 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="flex items-center justify-center"
      >
        {renderStageVisual(variant, accent, step, reducedMotion)}
      </motion.div>
    </AnimatePresence>
  );

  const content = (
    <div
      className={`flex items-center justify-center rounded-2xl border border-border bg-gradient-to-br ${accent.visualGlow} ${shellPadding[size]}`}
    >
      {inner}
    </div>
  );

  if (reducedMotion) {
    return content;
  }

  return (
    <motion.div {...motionPulse} className="flex items-center justify-center">
      {content}
    </motion.div>
  );
};
